// Verde "Common Expressions" pillar — figurative phrases whose meaning differs
// from their literal words (e.g. `quedarse sin` literally "to remain without",
// but really "to run out of"). These trip up learners precisely because the
// word-for-word reading is misleading, so each card shows three layers:
//
//   1. the expression,
//   2. what it *sounds like* word-for-word (the literal trap), and
//   3. what it *actually means*.
//
// This is a STATIC local-data pillar (like tenses / reference) — no PocketBase
// collection, no seed, no pins/notes. It ships entirely in the bundle.
//
// Each row is a terse tuple so the data reads like a table:
//   [ spanish, literal_gloss, meaning, example_es, example_en, emoji ]
// The literal_gloss is the deliberately-awkward word-for-word reading; meaning
// is the natural English. Groupings are by the anchoring verb so learners see
// the family resemblance (all the `dar …` phrases together, etc.).

const GROUPS = [
  // ---- tener (to have) -------------------------------------------------
  {
    category: "tener", rows: [
      ["tener ganas de", "to have desires of", "to feel like / to be eager to", "Tengo ganas de viajar este verano.", "I feel like travelling this summer.", "🤩"],
      ["tener prisa", "to have hurry", "to be in a hurry", "Tengo prisa, hablamos luego.", "I'm in a hurry, let's talk later.", "⏱️"],
      ["tener razón", "to have reason", "to be right", "Tienes razón, me equivoqué.", "You're right, I was wrong.", "✅"],
      ["tener cuidado", "to have care", "to be careful", "Ten cuidado con el escalón.", "Be careful with the step.", "⚠️"],
      ["tener lugar", "to have place", "to take place / to happen", "La reunión tiene lugar el lunes.", "The meeting takes place on Monday.", "📍"],
      ["tener en cuenta", "to have in account", "to take into account / to bear in mind", "Ten en cuenta el tráfico.", "Bear the traffic in mind.", "🧮"],
    ],
  },

  // ---- dar (to give) ---------------------------------------------------
  {
    category: "dar", rows: [
      ["dar igual", "to give equal", "to not matter / to be all the same", "Me da igual el color.", "I don't mind the colour.", "🤷"],
      ["darse cuenta de", "to give oneself account of", "to realise / to notice", "Me di cuenta del error a tiempo.", "I realised the mistake in time.", "💡"],
      ["dar la vuelta", "to give the turn", "to turn around / to flip over", "Da la vuelta a la tortilla.", "Flip the omelette over.", "🔄"],
      ["dar a luz", "to give to light", "to give birth", "Dio a luz a gemelos.", "She gave birth to twins.", "👶"],
      ["dar con", "to give with", "to find / to come across", "Por fin di con la solución.", "I finally hit on the solution.", "🎯"],
      ["darse prisa", "to give oneself hurry", "to hurry up", "¡Date prisa, que llegamos tarde!", "Hurry up, we're going to be late!", "🏃"],
      ["dar las gracias", "to give the thanks", "to thank (someone)", "Le di las gracias al conductor.", "I thanked the driver.", "🙏"],
    ],
  },

  // ---- hacer (to do / make) --------------------------------------------
  {
    category: "hacer", rows: [
      ["hacer caso", "to make case", "to pay attention / to listen to", "El niño no me hace caso.", "The child doesn't listen to me.", "👂"],
      ["hacer falta", "to make lack", "to be needed / to be necessary", "Hace falta más tiempo.", "More time is needed.", "🧩"],
      ["hacerse el tonto", "to make oneself the fool", "to play dumb", "No te hagas el tonto conmigo.", "Don't play dumb with me.", "🙄"],
      ["hacer cola", "to make tail", "to queue / to stand in line", "Hicimos cola durante una hora.", "We queued for an hour.", "🧍"],
      ["hacer las paces", "to make the peaces", "to make up / to reconcile", "Discutieron, pero hicieron las paces.", "They argued, but they made up.", "🤝"],
      ["hacer puente", "to make bridge", "to take a long weekend", "El viernes hacemos puente.", "We're taking Friday off for a long weekend.", "🌉"],
    ],
  },

  // ---- echar (to throw) ------------------------------------------------
  {
    category: "echar", rows: [
      ["echar de menos", "to throw of less", "to miss (someone or something)", "Te echo mucho de menos.", "I miss you a lot.", "💔"],
      ["echar una mano", "to throw a hand", "to lend a hand / to help out", "¿Me echas una mano con esto?", "Can you give me a hand with this?", "✋"],
      ["echarse a (llorar/reír)", "to throw oneself to", "to suddenly start (crying/laughing)", "Se echó a llorar de repente.", "She suddenly burst into tears.", "😭"],
      ["echar a perder", "to throw to lose", "to spoil / to ruin", "La lluvia echó a perder el plan.", "The rain ruined the plan.", "🌧️"],
      ["echar la culpa", "to throw the blame", "to blame (someone)", "Siempre me echa la culpa a mí.", "He always blames me.", "🫵"],
    ],
  },

  // ---- quedar / quedarse (to remain, to stay) --------------------------
  {
    category: "quedar", rows: [
      ["quedarse sin", "to remain without", "to run out of", "Me quedé sin batería en el móvil.", "I ran out of battery on my phone.", "🔋"],
      ["quedar con", "to remain with", "to arrange to meet (someone)", "Quedé con Ana a las seis.", "I arranged to meet Ana at six.", "🤝"],
      ["quedar en", "to remain in", "to agree to (do something)", "Quedamos en vernos mañana.", "We agreed to meet tomorrow.", "📆"],
      ["quedarse", "to remain oneself", "to stay", "Hoy me quedo en casa.", "Today I'm staying home.", "🏠"],
      ["quedar bien / mal", "to remain well / badly", "to make a good / bad impression", "Quiero quedar bien con sus padres.", "I want to make a good impression on his parents.", "🌟"],
      ["quedarle bien (a uno)", "to remain well to one", "to suit / to look good on", "Ese abrigo te queda muy bien.", "That coat really suits you.", "🧥"],
    ],
  },

  // ---- ponerse (to put oneself) ----------------------------------------
  {
    category: "ponerse", rows: [
      ["ponerse a (hacer algo)", "to put oneself to", "to start / to set about (doing)", "Se puso a estudiar después de cenar.", "He started studying after dinner.", "🏁"],
      ["ponerse las pilas", "to put on the batteries", "to get one's act together", "Ponte las pilas, queda poco tiempo.", "Get your act together, there's little time left.", "🔋"],
      ["ponerse de acuerdo", "to put oneself of agreement", "to reach an agreement", "No se ponen de acuerdo en nada.", "They can't agree on anything.", "🤝"],
      ["ponerse rojo", "to put oneself red", "to blush / to go red", "Se puso rojo de vergüenza.", "He went red with embarrassment.", "😳"],
    ],
  },

  // ---- ser / estar -----------------------------------------------------
  {
    category: "ser-estar", rows: [
      ["estar de moda", "to be of fashion", "to be in fashion / trendy", "Ese estilo está de moda otra vez.", "That style is in fashion again.", "👗"],
      ["estar harto de", "to be fed up of", "to be sick of / fed up with", "Estoy harto de esperar.", "I'm fed up of waiting.", "😤"],
      ["ser pan comido", "to be eaten bread", "to be a piece of cake / very easy", "El examen fue pan comido.", "The exam was a piece of cake.", "🍞"],
      ["estar en las nubes", "to be in the clouds", "to have one's head in the clouds", "Estás en las nubes hoy.", "You're daydreaming today.", "☁️"],
      ["estar al tanto", "to be at the so-much", "to be up to date / aware", "Estoy al tanto de las noticias.", "I'm up to date with the news.", "📰"],
      ["ser un rollo", "to be a roll", "to be boring / a drag", "La charla fue un rollo enorme.", "The talk was a huge drag.", "😴"],
    ],
  },

  // ---- idioms & sayings ------------------------------------------------
  {
    category: "idioms", rows: [
      ["costar un ojo de la cara", "to cost an eye from the face", "to cost an arm and a leg", "El coche costó un ojo de la cara.", "The car cost an arm and a leg.", "👁️"],
      ["no tener pelos en la lengua", "to not have hairs on the tongue", "to not mince words / to be blunt", "Mi jefa no tiene pelos en la lengua.", "My boss doesn't mince her words.", "👅"],
      ["tomar el pelo", "to take the hair", "to pull someone's leg / to tease", "¿Me estás tomando el pelo?", "Are you pulling my leg?", "😜"],
      ["meter la pata", "to put in the paw", "to put one's foot in it / to mess up", "Metí la pata en la reunión.", "I put my foot in it at the meeting.", "🦶"],
      ["estar como una cabra", "to be like a goat", "to be crazy / nuts", "Ese tío está como una cabra.", "That guy is completely nuts.", "🐐"],
      ["dar en el clavo", "to give on the nail", "to hit the nail on the head", "Diste en el clavo con esa idea.", "You hit the nail on the head with that idea.", "🔨"],
      ["ser uña y carne", "to be nail and flesh", "to be inseparable / thick as thieves", "Esos dos son uña y carne.", "Those two are inseparable.", "👫"],
      ["ponerse las botas", "to put on the boots", "to stuff oneself / to do very well", "Nos pusimos las botas en la cena.", "We stuffed ourselves at dinner.", "🥾"],
      ["importar un pimiento", "to matter a pepper", "to not care at all", "Me importa un pimiento lo que digan.", "I couldn't care less what they say.", "🫑"],
      ["quedarse de piedra", "to remain of stone", "to be stunned / dumbfounded", "Me quedé de piedra con la noticia.", "I was stunned by the news.", "🗿"],
      ["tirar la toalla", "to throw the towel", "to give up / to throw in the towel", "No tires la toalla tan pronto.", "Don't give up so soon.", "🏳️"],
      ["buscarle tres pies al gato", "to look for three feet on the cat", "to overcomplicate / to split hairs", "No le busques tres pies al gato.", "Don't overcomplicate things.", "🐈"],
    ],
  },
];

// Category registry — drives the browse view's filter chips and section headers.
// Order here is the canonical section order on the page.
export const EXPRESSION_CATEGORIES = [
  { id: "tener", label: "With tener", emoji: "🫳" },
  { id: "dar", label: "With dar", emoji: "🎁" },
  { id: "hacer", label: "With hacer", emoji: "🔨" },
  { id: "echar", label: "With echar", emoji: "🤲" },
  { id: "quedar", label: "With quedar", emoji: "📌" },
  { id: "ponerse", label: "With ponerse", emoji: "🔄" },
  { id: "ser-estar", label: "With ser & estar", emoji: "🪞" },
  { id: "idioms", label: "Idioms & sayings", emoji: "🎭" },
];

function buildExpressions() {
  const out = [];
  let rank = 1;
  for (const block of GROUPS) {
    for (const [spanish, literal, meaning, exEs, exEn, emoji] of block.rows) {
      out.push({
        spanish,
        literal,
        meaning,
        example_es: exEs,
        example_en: exEn,
        emoji,
        category: block.category,
        rank: rank++,
      });
    }
  }
  return out;
}

// Flattened, ranked list — the single source for the browse view.
export const EXPRESSIONS = buildExpressions();
